// RESONATE_PERF_V3 — framer-motion replaced with IntersectionObserver + CSS
// transitions. framer-motion was ~40KB gz in the critical bundle and its
// useScroll/whileInView machinery caused forced reflows during LCP paint.
// API is unchanged: Reveal / StaggerGroup / StaggerItem with the same props.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const HIDDEN = {
  up: "translateY(36px)",
  left: "translateX(-60px)",
  right: "translateX(60px)",
  scale: "scale(0.92)",
  blur: "translateY(24px) scale(0.96)",
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Observe an element; flip to true when `amount` of it is in view. */
export const useInView = (ref, amount, once) => {
  // Start visible when we can't observe (SSR, old browsers, reduced motion).
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === "undefined" || prefersReducedMotion()
  );

  useEffect(() => {
    if (inView && once) return;
    if (typeof IntersectionObserver === "undefined") return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) io.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      // `amount` mirrors framer's viewport.amount (fraction of the element).
      { threshold: Math.min(Math.max(amount, 0), 1) }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, amount, once, inView]);

  return inView;
};

/**
 * Reveal - viewport-triggered fade/slide animation primitive.
 * variant: 'up' | 'left' | 'right' | 'scale' | 'blur'
 */
export const Reveal = ({
  children,
  delay = 0,
  variant = "up",
  className = "",
  amount = 0.2,
  duration = 0.8,
  once = true,
  ...rest
}) => {
  const ref = useRef(null);
  const shown = useInView(ref, amount, once);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : HIDDEN[variant] || HIDDEN.up,
        transition: `opacity ${duration}s ${EASE} ${delay}s, transform ${duration}s ${EASE} ${delay}s`,
        willChange: shown ? undefined : "opacity, transform",
      }}
      {...rest}
    >
      {children}
    </div>
  );
};

const StaggerCtx = createContext(null);

export const StaggerGroup = ({
  children,
  className = "",
  stagger = 0.08,
  amount = 0.15,
  once = true,
}) => {
  const ref = useRef(null);
  const shown = useInView(ref, amount, once);
  // Items self-register in mount order to derive their transition delay.
  const counter = useRef(0);
  const ctx = useMemo(
    () => ({
      shown,
      stagger,
      nextIndex: () => counter.current++,
    }),
    [shown, stagger]
  );

  return (
    <StaggerCtx.Provider value={ctx}>
      <div ref={ref} className={className}>
        {children}
      </div>
    </StaggerCtx.Provider>
  );
};

export const StaggerItem = ({
  children,
  className = "",
  variant = "up",
  duration = 0.7,
}) => {
  const ctx = useContext(StaggerCtx);
  const indexRef = useRef(null);
  if (indexRef.current === null) {
    indexRef.current = ctx ? ctx.nextIndex() : 0;
  }
  const shown = ctx ? ctx.shown : true;
  const delay = 0.05 + indexRef.current * (ctx ? ctx.stagger : 0);
  const hidden =
    variant === "blur" ? "translateY(18px) scale(0.97)" : HIDDEN[variant] || HIDDEN.up;

  return (
    <div
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : hidden,
        transition: `opacity ${duration}s ${EASE} ${delay}s, transform ${duration}s ${EASE} ${delay}s`,
        willChange: shown ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};
